import { ReactElement, useCallback, useState } from 'react';

import {
   NotCopiedPrescription,
   VisitDraftPrescriptionsQuantityQuery,
   VisitPrescriptionCopyCommand,
   VisitPrescriptionCopyResult,
   VisitPrescriptionPackageQuery,
} from '@dreryk/egabinet-visit-frontend-api';
import { VisitId } from '@dreryk/egabinet-visit-published-language-api';

import { refetchCqrsQuery, useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';

import { VisitDiagnoses } from '~libs.shared.visit';

import { historicalVisitTranslationKeys } from '../../../translations';

import { NotCopiedPrescriptionReplacementModal } from './notCopiedPrescriptionReplacement/NotCopiedPrescriptionReplacementModal';

const t = historicalVisitTranslationKeys.copyPrescription;

type CopyPrescriptionActionRenderProps = {
   copyPrescription: () => Promise<unknown>;
};

type CopyPrescriptionActionProps = {
   readonly children: (renderProps: CopyPrescriptionActionRenderProps) => ReactElement;
   readonly prescriptionId: number;
   readonly targetVisitId: number;
   readonly patientId: number;
   readonly visitDiagnoses: VisitDiagnoses;
};

export const CopyPrescriptionAction = (props: CopyPrescriptionActionProps): ReactElement => {
   const [notCopiedPrescription, setNotCopiedPrescription] = useState<NotCopiedPrescription | null>(null);

   const { executeAsync: copyPrescription } = useCqrsCommand<VisitPrescriptionCopyResult, void>(
      () => {
         const command = new VisitPrescriptionCopyCommand();
         command.prescriptionIds = [props.prescriptionId];
         command.targetVisitId = VisitId.of(props.targetVisitId);
         return command;
      },
      {
         onSuccess: (result) => {
            if (result.notCopiedPrescriptions?.length) {
               setNotCopiedPrescription(result.notCopiedPrescriptions[0]);
            } else {
               dispatchSnackbarPositiveEvent(t.medicineCopiedSuccessfully);
               refetchCqrsQuery(VisitDraftPrescriptionsQuantityQuery);
               refetchCqrsQuery(VisitPrescriptionPackageQuery);
            }
         },
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(t.copyError);
         },
      }
   );

   const handleReplacementSuccess = useCallback(async (): Promise<void> => {
      await Promise.all([
         refetchCqrsQuery(VisitDraftPrescriptionsQuantityQuery),
         refetchCqrsQuery(VisitPrescriptionPackageQuery),
      ]);

      dispatchSnackbarPositiveEvent(t.medicineCopiedSuccessfully);
      setNotCopiedPrescription(null);
   }, []);

   const handleReplacementCancel = useCallback((): void => {
      setNotCopiedPrescription(null);
   }, []);

   return (
      <>
         {props.children({ copyPrescription: copyPrescription })}
         {notCopiedPrescription !== null && (
            <NotCopiedPrescriptionReplacementModal
               medicineId={notCopiedPrescription.medicineId}
               count={1}
               patientId={props.patientId}
               targetVisitId={props.targetVisitId}
               visitDiagnoses={props.visitDiagnoses}
               onSuccess={handleReplacementSuccess}
               onCancel={handleReplacementCancel}
            />
         )}
      </>
   );
};
